import { makeFieldSchema } from '@backstage/plugin-scaffolder-react';
import { TextField } from '@material-ui/core';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { useEffect, useState } from 'react';
import { ScaffolderField } from '@backstage/plugin-scaffolder-react/alpha';
import { AzureSubscription, useAzureSubscriptions } from './useAzureSubscriptions';

export const AzureSubscriptionPickerFieldSchema = makeFieldSchema({
  output: z =>
    z.object({
      subscriptionId: z.string().describe('The id of the selected Azure subscription'),
      displayName: z.string().describe('The display name of the selected Azure subscription'),
    }),
});

type AzureSubscriptionPickerProps = typeof AzureSubscriptionPickerFieldSchema.TProps;

const getSubscriptionFromForm = (form: any): AzureSubscription | null =>
  form?.subscriptionId && form?.displayName ? (form as AzureSubscription) : null;

const AzureSubscriptionPicker = (props: AzureSubscriptionPickerProps) => {
  const {
    schema: { title = 'Azure subscription', description = '' },
    onChange,
    rawErrors,
    required,
    formData,
    errors,
  } = props;

  const { subscriptions, isLoading, error, hasToken } = useAzureSubscriptions();
  const [selected, setSelected] = useState<AzureSubscription | null>(
    getSubscriptionFromForm(formData),
  );

  useEffect(() => {
    setSelected(getSubscriptionFromForm(formData));
  }, [formData]);

  useEffect(() => {
    const isValid = selected?.subscriptionId && selected?.displayName;
    onChange(isValid ? selected : undefined);
  }, [selected]); // eslint-disable-line react-hooks/exhaustive-deps

  const getHelperText = () => {
    if (!hasToken) return 'Waiting for Azure sign-in…';
    if (error instanceof Error) return error.message;
    return 'Select an Azure subscription from the list';
  };

  return (
    <ScaffolderField
      rawErrors={rawErrors}
      rawDescription={description}
      required={required}
      errors={errors}
    >
      <Autocomplete
        options={subscriptions ?? []}
        value={selected}
        getOptionLabel={sub => sub?.displayName ?? ''}
        getOptionSelected={(option, value) => option.subscriptionId === value.subscriptionId}
        onChange={(_, newValue) => setSelected(newValue)}
        loading={isLoading}
        disabled={!hasToken}
        renderInput={params => (
          <TextField
            {...params}
            required={required}
            label={title}
            error={Boolean(error)}
            helperText={getHelperText()}
          />
        )}
      />
    </ScaffolderField>
  );
};

export default AzureSubscriptionPicker;
